import { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isAppLang, normalizeAppLang } from "@u/appLang";

export default function LocaleLayout() {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Keep refs fresh so the i18n listener always sees current values
  const langRef = useRef(lang);
  const locationRef = useRef(location);

  useEffect(() => {
    langRef.current = lang;
    locationRef.current = location;
  });

  // Sync URL :lang → i18next on mount / route change
  useEffect(() => {
    if (lang && isAppLang(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    // Reflect the active locale on <html lang> for SEO/a11y. The static
    // index.html hardcodes "en"; without this every localized route lied.
    if (lang && isAppLang(lang)) document.documentElement.lang = lang;
  }, [lang, i18n]);

  // Sync i18next language change (e.g. dropdown) → URL
  useEffect(() => {
    const handler = (newLang: string) => {
      const currentLang = langRef.current;
      if (!currentLang || !isAppLang(newLang)) return;
      if (newLang === currentLang) return;
      const newPath = locationRef.current.pathname.replace(
        `/${currentLang}`,
        `/${newLang}`,
      );
      navigate(newPath, { replace: true });
    };

    i18n.on("languageChanged", handler);
    return () => { i18n.off("languageChanged", handler); };
  }, [i18n, navigate]);

  if (!lang || !isAppLang(lang)) {
    // Normalizado: con lang inválido (p. ej. /en-US/), redirigir a
    // /${i18n.language}/ reproducía la misma URL inválida → Navigate no-op →
    // página en blanco para todo visitante nuevo con locale regional.
    const rest = location.pathname.replace(/^\/[^/]+/, "");
    return <Navigate to={`/${normalizeAppLang(i18n.language)}${rest || "/"}`} replace />;
  }

  return <Outlet />;
}
